<template>
  <v-data-table-server
    v-if="totalItems > 0"
    v-model:options="options"
    dense
    :headers="headers"
    :loading="loading"
    :items="items"
    :items-length="debugTotalItems"
    class="elevation-1"
    :items-per-page-options="footerProps.itemsPerPageOptions"
    @update:options="updateTable"
  >
    <template #top>
      <v-card-title>
        <v-toolbar flat>
          <v-toolbar-title>Admin User List</v-toolbar-title>

          <v-spacer />

          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            label="Search"
            single-line
            hide-details
            @input="searchTable()"
          />

          <v-spacer />

          <UserDeleteModal
            v-model="dialogDelete"
            :edited-item="editedItem"
            @close-modal="closeModal('dialogDelete')"
            @item-deleted="itemDeleted()"
            @click:outside="clearState()"
          />

          <UserFormModal
            v-model="dialogForm"
            :edited-item="editedItem"
            @close-modal="closeModal('dialogForm')"
            @item-saved="itemSaved()"
            @click:outside="clearState()"
          />

          <user-password-modal
            v-model="dialogPassword"
            :edited-item="editedItem"
            @close-modal="closeModal('dialogPassword')"
            @item-saved="itemSaved()"
            @click:outside="clearState()"
          />

          <User2faModal
            v-model="dialog2fa"
            :edited-item="editedItem"
            @close-modal="closeModal('dialog2fa')"
            @item-saved="itemSaved()"
            @click:outside="clearState()"
          />

          <v-btn
            v-if="hasPermission('create user-admin::user')"
            dark
            class="mb-2 btn-bg-deep-blue"
            @click="dialogForm = true"
          >
            <v-icon>mdi-plus</v-icon>
            New User
          </v-btn>
          <disabled-for-lack-of-role-btn
            v-else
            text="New User"
            permission="create user-admin::user"
            :x-small="false"
            button-class="mb-2"
          />
        </v-toolbar>
      </v-card-title>
    </template>

    <template #[`item.roles`]="{ item }">
      {{ roleNames(item.roles) }}
    </template>

    <template #[`item.create_date`]="{ item }">
      {{ dateFromSQL(item.create_date) }}
    </template>

    <template #[`item.deleted_at`]="{ item }">
      {{ dateFromISO(item.deleted_at) }}
    </template>

    <template #[`item.actions`]="{ item }">
      <v-icon
        v-if="hasPermission('edit user-admin::user')"
        :id="item"
        class="mr-10"
        :disabled="!!item.deleted_at"
        @click="editItem(item)"
      >
        mdi-pencil-outline
      </v-icon>
      <disabled-for-lack-of-role-btn
        v-else
        text=""
        icon="mdi-pencil-outline"
        :text-button="true"
        permission="edit user-admin::user"
        :x-small="false"
        button-class="mr-10"
      />

      <v-icon
        v-if="hasPermission('edit user-admin::user')"
        :id="item"
        class="mr-10"
        :disabled="!!item.deleted_at"
        @click="updatePassword(item)"
      >
        mdi-lock-outline
      </v-icon>
      <disabled-for-lack-of-role-btn
        v-else
        text=""
        icon="mdi-lock-outline"
        :text-button="true"
        permission="edit user-admin::user"
        :x-small="false"
        button-class="mr-10"
      />

      <v-icon
        v-if="hasPermission('edit user-admin::user')"
        :id="item"
        class="mr-10"
        :disabled="!!item.deleted_at"
        :color="item.mfa ? 'green' : 'red'"
        @click="reset2fa(item)"
      >
        mdi-two-factor-authentication
      </v-icon>
      <disabled-for-lack-of-role-btn
        v-else
        text=""
        icon="mdi-two-factor-authentication"
        :text-button="true"
        permission="edit user-admin::user"
        :x-small="false"
        button-class="mr-10"
      />

      <v-icon
        v-if="hasPermission('delete user-admin::user')"

        :disabled="!!item.deleted_at"
        @click="deleteItem(item)"
      >
        mdi-delete-outline
      </v-icon>
      <disabled-for-lack-of-role-btn
        v-else
        text=""
        icon="mdi-delete-outline"
        :text-button="true"
        permission="delete user-admin::user"
        :x-small="false"
        button-class="mr-10"
      />
      <v-icon
        v-if="hasPermission('view user-token')"
        :disabled="item.tokens"
        @click="viewToken(item)"
      >
        mdi-api
      </v-icon>
      <disabled-for-lack-of-role-btn
        v-else
        text=""
        icon="mdi-api"
        :text-button="true"
        permission="view user-token"
        :x-small="false"
        button-class="mr-10"
      />
    </template>
  </v-data-table-server>

  <v-card
    v-else
    class="elevation-1"
  >
    <v-card-text class="text-center pa-10">
      <v-progress-circular
        indeterminate
        color="primary"
        size="64"
      />
      <div class="mt-4 text-h6">
        Loading users...
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapActions } from 'vuex';
import { DateTime } from 'luxon';
import dataTableMixin from '@/mixins/dataTableMixin.js';
import UserFormModal from '@/views/UserAdmin/User/UserFormModal.vue';
import UserDeleteModal from './UserDeleteModal.vue';
import UserPasswordModal from './UserPasswordModal.vue';
import User2faModal from './User2faModal.vue';
import DisabledForLackOfRoleBtn from '../../DisabledForLackOfRoleBtn.vue';

export default {
  components: {
    UserFormModal,
    UserDeleteModal,
    UserPasswordModal,
    User2faModal,
    DisabledForLackOfRoleBtn,
  },
  mixins: [
    dataTableMixin,
  ],

  data: () => ({
    headers: [
      { title: 'First Name', value: 'first_name' },
      { title: 'Last Name', value: 'last_name' },
      { title: 'Username', value: 'username' },
      { title: 'Email', value: 'email' },
      { title: 'Roles', value: 'roles' },
      { title: 'Activated', value: 'create_date' },
      { title: 'Deactivated', value: 'deleted_at' },
      { title: 'Actions', value: 'actions', sortable: false },
    ],

    dialogDelete: false,
    dialogForm: false,
    dialogPassword: false,
    dialog2fa: false,
    editedItem: {
      id: null,
    },
  }),

  computed: {
    debugTotalItems() {
      console.log('🔍 Vuetify reading server-items-length:', this.totalItems);
      return this.totalItems;
    },
  },

  created() {
    this.getRoles({
      perPage: this.options.itemsPerPage,
      page: this.options.page,
      ...this.sortParams,
      search: this.search,
    });
    this.getAllRoles({});
    this.updateTable();
  },

  methods: {
    ...mapActions('userPermission', ['getRoles', 'getAllRoles']),
    closeModal(modal) {
      this[modal] = false;
      this.clearState();
    },
    deleteItem(item) {
      this.editedIndex = this.items.indexOf(item);
      this.editedItem = { ...item };
      this.dialogDelete = true;
    },
    editItem(item) {
      this.editedIndex = this.items.indexOf(item);
      this.editedItem = { ...item };
      this.dialogForm = true;
    },
    updatePassword(item) {
      this.editedIndex = this.items.indexOf(item);
      this.editedItem = { ...item };
      this.dialogPassword = true;
    },
    reset2fa(item) {
      this.editedIndex = this.items.indexOf(item);
      this.editedItem = { ...item };
      this.dialog2fa = true;
    },
    itemDeleted() {
      this.items = [];
      this.updateTable();
    },
    itemSaved() {
      this.items = [];
      this.updateTable();
    },
    updateTable() {
      this.loading = true;

      return this.$axios
        .get('api/v1/user', {
          params: {
            perPage: this.options.itemsPerPage,
            page: this.options.page,
            ...this.sortParams,
            search: this.search,
          },
        })
        .then((res) => {
          console.log('✅ API RESPONSE:', {
            totalFromAPI: res.data.total,
            itemsCount: res.data.data?.length,
            perPage: res.data.per_page,
            fullResponse: res.data,
          });
          this.loading = false;
          this.items = res.data.data;
          this.totalItems = res.data.total;
          console.log('✅ AFTER SET - totalItems:', this.totalItems, 'items length:', this.items?.length);
        })
        .catch((error) => {
          console.error('❌ API ERROR:', error);
          this.loading = false;
        });
    },
    clearState() {
      this.$nextTick(() => {
        this.editedItem = {};
        this.editedIndex = -1;
      });
    },
    roleNames(roles) {
      // find the group objects that match the id of this users allowed groups
      const items = [];
      if (roles?.length > 0) {
        roles.forEach((element) => {
          items.push(element.name);
        });

        return items.toString();
      }

      return '';
    },
    dateFromSQL(date) {
      return date === null ? '' : DateTime.fromSQL(date).toLocaleString(DateTime.DATETIME_MED);
    },
    dateFromISO(date) {
      return date === null ? '' : DateTime.fromISO(date).toLocaleString(DateTime.DATETIME_MED);
    },
    viewToken(item) {
      this.$router.push({
        name: 'user-tokens',
        params: {
          id: item.id,
          user: item.username,
        },
        query: {
          user: item.username,
        },
      });
    },
  },
};
</script>
